import ModuleContext from 'CMFW/context/ModuleContext';
import { SelectLayout } from 'components/selects/SelectLayout';
import { IRefIds } from 'constants/refs';
import { getCacheKey } from 'helpers/cache';
import { useCacheData } from 'hooks/useCacheData';
import { useCurrentItemValue } from 'hooks/useCurrentItemValue';
import React, { useContext, useMemo } from 'react';
import VocCategorySelectModel from '../../models/VocCategorySelectModel';

const KEY = 'parent';

type IProps = {
  className?: string;
};

type ISelectData = { value: number; label: string };

export const VocCategoryRootSelect: React.FC<IProps> = (props) => {
  const module = useContext(ModuleContext);
  const moduleItemValue: number = module.getItemValue(KEY);
  const currentId: number = module.getItemValue('id') ?? 0;

  const [currentValue, setCurrentValue] = useCurrentItemValue(moduleItemValue, 0);

  const cacheKey = getCacheKey(IRefIds.vocabularyCategories);
  const dataModel = useMemo(() => new VocCategorySelectModel(), []);
  const cacheSelectData = useCacheData(cacheKey, dataModel.get.bind(dataModel));

  const handleChange = (vocCategory: ISelectData) => {
    setCurrentValue(vocCategory.value);
    module.updateItemValue(KEY, vocCategory.value);
  };

  const selectData: null | ISelectData[] = useMemo(() => {
    if (cacheSelectData === null) {
      return null;
    }

    // A category cannot have itself or one of its descendants as parent.
    const excludedIds = new Set<number>();
    if (currentId) {
      excludedIds.add(currentId);
      let changed = true;
      while (changed) {
        changed = false;
        cacheSelectData.forEach((vocCategory) => {
          if (excludedIds.has(vocCategory.parent) && !excludedIds.has(vocCategory.id)) {
            excludedIds.add(vocCategory.id);
            changed = true;
          }
        });
      }
    }

    let data = cacheSelectData
      .filter((vocCategory) => !excludedIds.has(vocCategory.id))
      .map((vocCategory) => ({
        label: vocCategory.fullPath,
        value: vocCategory.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    data = [{ label: 'ROOT', value: 0 }, ...data];

    return data;
  }, [cacheSelectData, currentId]);

  return (
    <SelectLayout
      className={props.className}
      data={selectData}
      onChange={handleChange}
      label="Parent"
      optionLabelKey="label"
      optionValueKey="value"
      currentValue={currentValue}
      originalValue={moduleItemValue}
    />
  );
};
